/**
 * @module coverage
 * @purpose Pure, factual derivations that partition Stage-6 hypotheses into promoted
 *   (full workflow card) vs library (compact side-list) and compute an indicator
 *   coverage ratio. No JSX, no scoring.
 * @depends types/reasoning
 * @dependedOnBy HypothesesView, LibraryPanel, LibraryPanelRow
 *
 * @rules
 * - INVARIANT: Evidenx does not score. "Coverage" is a factual inventory count of
 *   observed-vs-declared indicators — never a confidence, likelihood, or probability.
 * - Deterministic: outputs depend only on the hypothesis' explanation.basis counts.
 */

import type { Hypothesis } from '../../../../../types/reasoning'

/**
 * Factual indicator coverage for one hypothesis: how many declared observables were
 * present vs how many the predicate declared for this role.
 *
 * @behaviour Not a score. present + absent counts are observable-granular and mutually
 *   exclusive, so declared = present + absencesSurfaced.
 * @errors n/a
 */
export interface HypothesisCoverage {
  present: number
  declared: number
}

/**
 * The three factual states a library (non-promoted) row can be in.
 *
 * @behaviour Mirrors the neutral facts already in explanation.basis — never a ranking.
 * @errors n/a
 */
export type CoverageStatus = 'no_indicators' | 'required_absent' | 'partial'

/**
 * Compute the indicator coverage ratio from the explanation basis.
 *
 * @behaviour present = basis.distinctMatches (observables judged Present).
 *   declared = distinctMatches + absencesSurfaced. Each declared observable increments
 *   exactly one of these in the backend (stage6/explanation.rs::build_basis), so the sum
 *   is the declared-observable count for this role. The numerator is distinctMatches — NOT
 *   corroboratingArtifactTypes.length, which counts distinct artefact *types* (can be fewer).
 * @errors n/a
 */
export function computeCoverage(hypothesis: Hypothesis): HypothesisCoverage {
  const basis = hypothesis.explanation.basis
  return {
    present: basis.distinctMatches,
    declared: basis.distinctMatches + basis.absencesSurfaced,
  }
}

/**
 * Stable identity for a predicate: name + version. A predicate emits one hypothesis row per
 * role (source/target), and promotion is decided for the predicate as a whole, not per role.
 *
 * @behaviour Rows sharing this key belong to the same predicate group.
 * @errors n/a
 */
export function predicateKey(hypothesis: Hypothesis): string {
  return `${hypothesis.predicateName}@${hypothesis.predicateVersion}`
}

/**
 * Decide whether a whole predicate (all its role rows) earns a prominent workflow card.
 *
 * @behaviour Required-aware, evaluated across roles because a predicate's defining criterion
 *   may live on a single role. If ANY row declares a required observable, the predicate is
 *   promoted only when that required observable actually matched on some role — a lone
 *   corroborating hit (e.g. an ambient SMB 445 netstat flow on the psexec target role) must NOT
 *   raise a card while the required source-side prefetch is absent. If NO row declares a required
 *   observable (a pure emit-on-any-match predicate such as attacker_toolkit_interim), any present
 *   indicator promotes. This deliberately refines the looser per-row "any indicator present" rule:
 *   live POC5 data showed a single ambient netstat match promoting psexec despite its required
 *   criterion being unmet.
 * @errors n/a
 */
export function isGroupPromoted(rows: Hypothesis[]): boolean {
  const declaresRequired = rows.some((row) => row.explanation.basis.requiredDeclared)
  if (declaresRequired) {
    return rows.some(
      (row) => row.explanation.basis.requiredDeclared && row.explanation.basis.requiredMatched,
    )
  }
  return rows.some((row) => row.explanation.basis.distinctMatches > 0)
}

/**
 * Classify a hypothesis into its factual coverage status (for library rows).
 *
 * @behaviour Precedence: no indicators present at all → no_indicators (most informative for a
 *   zero-match row, matches a "0 of N" ratio); else a declared-required observable missed →
 *   required_absent; else a partial set of non-required observables → partial. All three are
 *   neutral facts already encoded in explanation.basis, not a score.
 * @errors n/a
 */
export function coverageStatus(hypothesis: Hypothesis): CoverageStatus {
  const basis = hypothesis.explanation.basis
  if (basis.distinctMatches === 0) {
    return 'no_indicators'
  }
  if (basis.requiredDeclared && !basis.requiredMatched) {
    return 'required_absent'
  }
  return 'partial'
}

/**
 * Partition an ordered hypothesis list into promoted cards vs compact library rows, deciding
 * promotion per predicate (all role rows of a predicate share the same fate).
 *
 * @behaviour Groups rows by predicateKey in first-seen order (the list already arrives sorted by
 *   libraryOrder from the repository, so groups stay in library-presentation order). Each group is
 *   promoted or demoted as a whole via isGroupPromoted, then its rows are appended in their
 *   original order. A predicate whose required criterion is unmet (e.g. psexec on POC5) sends ALL
 *   its rows — source and target — to the library, even if one role caught an ambient match.
 * @errors n/a
 */
export function partitionHypotheses(hypotheses: Hypothesis[]): {
  promoted: Hypothesis[]
  library: Hypothesis[]
} {
  const groups = new Map<string, Hypothesis[]>()
  const order: string[] = []
  for (const hypothesis of hypotheses) {
    const key = predicateKey(hypothesis)
    const existing = groups.get(key)
    if (existing === undefined) {
      groups.set(key, [hypothesis])
      order.push(key)
    } else {
      existing.push(hypothesis)
    }
  }

  const promoted: Hypothesis[] = []
  const library: Hypothesis[] = []
  for (const key of order) {
    const rows = groups.get(key) ?? []
    if (isGroupPromoted(rows)) {
      promoted.push(...rows)
    } else {
      library.push(...rows)
    }
  }
  return { promoted, library }
}
